import { isEmpty } from 'lodash'
import { pad2 } from 'utils/string'

const TWENTY_FOUR_HOUR_MASK = /(0[0-9]|1[0-9]|2[0-3])/
const HOUR_MASK = /(0[1-9]|1[0-2])/
const MINUTES_MASK = /[0-5][0-9]/
const HOUR12_MASK = /(a|p)m/

const getHour12 = (hour = 0): string => {
  return hour < 12 ? 'am' : 'pm'
}

export const convertHour12ToHour24Time = (time = ''): string => {
  const [time12, hour12] = time.split(' ')
  const [hour, minutes] = time12.split(':')

  if (HOUR_MASK.test(hour) && MINUTES_MASK.test(minutes) && HOUR12_MASK.test(hour12)) {
    if (hour12 === 'am') {
      if (hour === '12') {
        return `00:${minutes}`
      }

      return `${hour}:${minutes}`
    } else if (hour === '12') {
      return `${hour}:${minutes}`
    }

    return `${parseInt(hour) + 12}:${minutes}`
  }

  return ''
}

export const convertHour24ToHour12Time = (time = ''): string => {
  const [hour, minutes] = time.split(':')
  const hourNb = parseInt(hour)
  const hour12 = getHour12(hourNb)

  if (TWENTY_FOUR_HOUR_MASK.test(hour) && MINUTES_MASK.test(minutes)) {
    if (hour === '00' || hour === '12') {
      return `12:${minutes} ${hour12}`
    }
    return `${pad2(hourNb % 12)}:${minutes} ${hour12}`
  }

  return ''
}

/**
 * Converts a 24-hour formatted time string to the total number of minutes since midnight.
 *
 * @param {string} [time=''] - The 24-hour formatted time string (e.g., "14:30").
 * @returns {number} The total number of minutes since midnight.
 *
 * @example
 * // Converting "14:30" to minutes
 * const minutes = convert24HourToMinutes("14:30");  // returns 870
 *
 * @example
 * // Converting "00:00" to minutes
 * const minutes = convert24HourToMinutes("12:00 am");  // returns 0
 *
 * @example
 * // Handling invalid time format
 * const minutes = convert24HourToMinutes("invalid");  // returns 0
 */
export const convert24HourToMinutes = (time = ''): number => {
  try {
    const [hour, minutes] = time.split(':')

    const hoursNb = Number(hour) || 0
    const minsNb = Number(minutes) || 0

    return (hoursNb % 24) * 60 + minsNb
  } catch (e) {
    return 0
  }
}

export interface ITimeRange {
  startHour: string
  endHour: string
}

export const timeRangeToString = ({ startHour, endHour }: ITimeRange): string => {
  const startHourIn12HourTimeFormat = convertHour24ToHour12Time(startHour)
  const endHourIn12HourTimeFormat = convertHour24ToHour12Time(endHour)

  if (startHour && endHour) {
    return `${startHourIn12HourTimeFormat} - ${endHourIn12HourTimeFormat}`
  } else if (startHour) {
    return `Starts at ${startHourIn12HourTimeFormat}`
  } else if (endHour) {
    return `Ends at ${endHourIn12HourTimeFormat}`
  }

  return ''
}

const TIME_REGEX = /^([01][0-9]|2[0-3]):([0-5][0-9])$/

export const sanitizeTime = (time: string): string => {
  if (!TIME_REGEX.test(time)) {
    return ''
  }

  return time
}

interface ISanitizeTimeRange extends ITimeRange {
  defaultTimeRange?: ITimeRange
}

/**
 * Sanitizes a time range object.
 * All times are expected to be in 24-hour format.
 *
 * @param {object} timeRange - The time range object.
 * @param {string} timeRange.startHour - The start hour.
 * @param {string} timeRange.endHour - The end hour.
 * @param {object} [defaultTimeRange] - The default time range object.
 * @param {string} defaultTimeRange.startHour - The default start hour.
 * @param {string} defaultTimeRange.endHour - The default end hour.
 *
 * @returns {object} The sanitized time range object.
 */
export const sanitizeTimeRange = ({ startHour, endHour, defaultTimeRange }: ISanitizeTimeRange): ITimeRange => {
  const sanitizedStartHour = sanitizeTime(startHour)
  const sanitizedEndHour = sanitizeTime(endHour)
  return {
    startHour: isEmpty(sanitizedStartHour) ? defaultTimeRange?.startHour || '' : sanitizedStartHour,
    endHour: isEmpty(sanitizedEndHour) ? defaultTimeRange?.endHour || '' : sanitizedEndHour
  }
}

export const dateToTimeString = (value: string, timezone: string): string => {
  if (!value) return ''

  const date = new Date(value)

  if (!isFinite(date.getTime())) {
    return 'Invalid Date'
  }

  return new Intl.DateTimeFormat('es-MX', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
    timeZone: timezone
  }).format(date)
}

/**
 * Substracts minutes from a time string.
 * @param {string} time - The time string (e.g., "02:30").
 * @param {number} minutes - The number of minutes to substract.
 *
 * @returns
 * - {string} updatedTime - The updated time string.
 * - {number} dayDifference - The number of days the time has been substracted.
 *
 */
export const subtractMinutesWithDayShift = (
  time: string,
  minutes: number
): { updatedTime: string; dayDifference: number } => {
  const [hours, mins] = time.split(':')
  let totalMinutes = parseInt(hours) * 60 + parseInt(mins) - minutes
  const dayDifference = Math.floor(totalMinutes / (24 * 60))
  if (totalMinutes < 0) {
    totalMinutes += 24 * 60
  }
  const newHours = Math.floor(totalMinutes / 60) % 24
  const newMins = totalMinutes % 60

  return {
    updatedTime: `${pad2(newHours)}:${pad2(newMins)}`,
    dayDifference: dayDifference
  }
}

export const createTimeInterval = ({
  interval,
  isEndOfInterval = false
}: {
  interval: number
  isEndOfInterval?: boolean
}): string[] => {
  return Array.from({ length: (24 * 60) / interval }, (_, index) => {
    if (isEndOfInterval) {
      index += 1
    }
    const hours = Math.floor((index * interval) / 60)
    const minutes = (index * interval) % 60

    let time = `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`

    if (isEndOfInterval) {
      time = subtractMinutesWithDayShift(time, 1).updatedTime
    }
    return time
  })
}
